import React, { useEffect, useMemo } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { ColorSchemeScript } from '@mantine/core'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { CCMSProvider, AuthProvider, useHttpClient, useAuth, PresenceStatus } from '@actschurch/ccms-sdk'
import { CCMS_BASE_URL, AUTH_CONFIG, getWsUrl } from './config/ccms'
import { createSecureStorage } from './utils/secureStorage'
import App from './App'
import './index.css'
import './theme/accessibility.css'
import { PreferencesProvider } from './contexts'
import { usePreferencesStore } from './stores'
import { AlertProvider, ModalsProvider, ThemeProvider } from './components/ui'
import { appTheme, getColorScheme } from './theme/theme'
import { usePresenceManager } from './hooks/usePresenceManager'
import { useDeviceRegistration } from './hooks/useDeviceRegistration'
import { usePreferenceLoader } from './hooks/usePreferenceLoader'
import ScreenLockModal from './layouts/components/ScreenLockModal'
import IdleWarningToast from './layouts/components/IdleWarningToast'
import { HelpProvider } from './components/help'

// Storage key for persisting lock state across page refreshes
const SCREEN_LOCK_STORAGE_KEY = 'ccms_screen_locked'

// Create Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

// Auto-register all menu providers (must be before React mounts)
import './domain/providers'


// CCMS SDK Configuration
const ccmsConfig = {
  baseUrl: CCMS_BASE_URL,
  logsUrl: getWsUrl('logs'),
  dashboardsUrl: getWsUrl('dashboards'),
  // Auto-inject token for WebSocket authentication (logs, presence, etc.)
  // Uses same pattern as PresenceManager (see lines 112-133)
  getToken: async () => {
    try {
      const storage = createSecureStorage()
      const user = await storage.getUser()
      if (!user || !user.accessToken) {
        return null
      }
      return user.accessToken
    } catch (error) {
      console.error('[CCMSProvider] Error getting token:', error)
      return null
    }
  },
  debug: import.meta.env.DEV,
}

// Auth Provider Wrapper (needs HttpClient from CCMSProvider)
function AppWithAuth({ children }: { children: React.ReactNode }) {
  const httpClient = useHttpClient()
  const storage = React.useMemo(() => createSecureStorage(), [])
  const presenceManager = usePresenceManager()

  return (
    <AuthProvider
      httpClient={httpClient}
      storage={storage}
      refreshThresholdSeconds={AUTH_CONFIG.refreshThresholdSeconds}
      autoRefresh={AUTH_CONFIG.autoRefresh}
      onLogin={(user) => {
        console.log('[Auth] User logged in:', user.email)
        // Clear any stale lock state on fresh login
        try {
          sessionStorage.removeItem(SCREEN_LOCK_STORAGE_KEY)
        } catch {
          // Ignore storage errors
        }
      }}
      onLogout={() => {
        console.log('[Auth] User logged out')

        // Clear lock state on logout
        try {
          sessionStorage.removeItem(SCREEN_LOCK_STORAGE_KEY)
        } catch {
          // Ignore storage errors
        }

        // Disconnect PresenceManager on logout
        if (presenceManager.manager) {
          presenceManager.disconnect().catch(console.error)
        }
      }}
      onRefresh={(user) => {
        console.log('[Auth] Token refreshed for:', user.email)
        // Reconnect WebSockets with new token
        if (presenceManager.manager && presenceManager.isConnected) {
          console.log('[Auth] Reconnecting PresenceManager with new token')
          presenceManager.disconnect()
            .then(() => presenceManager.connect())
            .then(() => console.log('[Auth] PresenceManager reconnected successfully'))
            .catch((error) => console.error('[Auth] Failed to reconnect PresenceManager:', error))
        }
      }}
      onRefreshExhausted={() => {
        console.warn('[Auth] Session expired - all refresh attempts failed. Redirecting to login.')
      }}
      onError={(error) => {
        console.error('[Auth] Error:', error)
      }}
    >
      {children}
    </AuthProvider>
  )
}

// Device Registration Wrapper (registers browser for push notifications after login)
function AppWithDeviceRegistration({ children }: { children: React.ReactNode }) {
  const { state: authState } = useAuth()
  const deviceRegistration = useDeviceRegistration()

  // Register device when user becomes authenticated
  React.useEffect(() => {
    if (authState.isAuthenticated && authState.user) {
      // Auto-register device on login (non-blocking)
      deviceRegistration.autoRegisterOnLogin()
    } else {
      // Reset on logout
      deviceRegistration.resetRegistration()
    }
  }, [authState.isAuthenticated, authState.user, deviceRegistration])

  return <>{children}</>
}

// Preference Loader Wrapper (loads user preferences from server after login)
function AppWithPreferenceLoader({ children }: { children: React.ReactNode }) {
  const { state: authState } = useAuth()
  const preferenceLoader = usePreferenceLoader()

  React.useEffect(() => {
    if (authState.isAuthenticated && authState.user) {
      preferenceLoader.autoLoadOnLogin()
    } else {
      preferenceLoader.resetLoader()
    }
  }, [authState.isAuthenticated, authState.user, preferenceLoader])

  return <>{children}</>
}

// Presence Manager Wrapper (initializes PresenceManager with automatic features)
function AppWithPresence({ children }: { children: React.ReactNode }) {
  const { state: authState } = useAuth()
  const presenceManager = usePresenceManager()
  const storage = React.useMemo(() => createSecureStorage(), [])

  // Guard ref to prevent immediate re-lock after unlock
  const justUnlockedRef = React.useRef(false)

  // Initialize lock state from sessionStorage to persist across page refreshes
  const [isScreenLocked, setIsScreenLocked] = React.useState(() => {
    try {
      return sessionStorage.getItem(SCREEN_LOCK_STORAGE_KEY) === 'true'
    } catch {
      return false
    }
  })

  // Sync lock state to sessionStorage whenever it changes
  React.useEffect(() => {
    try {
      if (isScreenLocked) {
        sessionStorage.setItem(SCREEN_LOCK_STORAGE_KEY, 'true')
      } else {
        sessionStorage.removeItem(SCREEN_LOCK_STORAGE_KEY)
      }
    } catch (error) {
      console.error('[AppWithPresence] Failed to persist lock state:', error)
    }
  }, [isScreenLocked])

  // Initialize and connect PresenceManager when authenticated
  React.useEffect(() => {
    if (authState.isAuthenticated && authState.user) {
      const hubUrl = getWsUrl('presence')

      // Get token function - reads from secure storage
      const getToken = async () => {
        try {
          const user = await storage.getUser()
          if (!user || !user.accessToken) {
            console.warn('[PresenceManager] No token available')
            return null
          }
          return user.accessToken
        } catch (error) {
          console.error('[PresenceManager] Error getting token:', error)
          return null
        }
      }

      // Initialize PresenceManager
      presenceManager.initialize(hubUrl, getToken)

      // Connect with retry logic - ensures token is ready first
      const connectWithRetry = async (attempt = 1, maxAttempts = 3) => {
        try {
          // CRITICAL: Wait for token to be available BEFORE connecting
          const token = await getToken()
          if (!token) {
            throw new Error('No authentication token available')
          }

          // Token is ready, now connect
          await presenceManager.connect()
          console.log('[PresenceManager] Connected successfully')
        } catch (error: any) {
          console.error(`[PresenceManager] Connection attempt ${attempt} failed:`, error.message)

          // Check if it's an auth error (vs network error)
          const isAuthError = error?.message?.toLowerCase().includes('auth') ||
                             error?.message?.toLowerCase().includes('unauthorized') ||
                             error?.message?.toLowerCase().includes('token')

          if (isAuthError && attempt < maxAttempts) {
            // Retry with exponential backoff: 1s, 2s, 4s
            const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000)
            console.warn(`[PresenceManager] Retrying in ${delay}ms (${attempt + 1}/${maxAttempts})`)

            await new Promise(resolve => setTimeout(resolve, delay))
            return connectWithRetry(attempt + 1, maxAttempts)
          } else if (!isAuthError) {
            console.error('[PresenceManager] Non-auth error:', error)
          } else {
            console.error('[PresenceManager] Max retries reached, giving up')
          }
        }
      }

      // Start connection sequence
      connectWithRetry()
    }

    // Cleanup on unmount or logout
    return () => {
      if (presenceManager.manager && authState.isAuthenticated) {
        presenceManager.destroy().catch(console.error)
      }
    }
  }, [authState.isAuthenticated, authState.user, storage])

  // Handle idle state changes - lock screen when user becomes idle
  // Uses config.autoLockOnIdle from persisted store (user-configurable in settings)
  React.useEffect(() => {
    // Don't re-lock if user just unlocked (guard against isIdle still being true)
    if (justUnlockedRef.current) return

    if (presenceManager.config.autoLockOnIdle && presenceManager.isIdle && !isScreenLocked) {
      setIsScreenLocked(true)
    }
  }, [presenceManager.isIdle, isScreenLocked, presenceManager.config.autoLockOnIdle])

  // Handle screen unlock - reset idle state and reconnect
  const handleUnlock = async () => {
    // Set guard to prevent immediate re-lock
    justUnlockedRef.current = true

    // Reset idle timer in SDK (activity timestamp + idle state)
    if (presenceManager.manager) {
      presenceManager.manager.resetIdleTimer()
    }

    // Dismiss the lock screen
    setIsScreenLocked(false)

    // Update presence status back to Online
    try {
      await presenceManager.updateStatus(PresenceStatus.Online)
    } catch (error) {
      console.error('[AppWithPresence] Failed to update status after unlock:', error)
    }

    // Reconnect PresenceManager if disconnected
    if (presenceManager.manager && !presenceManager.isConnected) {
      try {
        await presenceManager.connect()
      } catch (error) {
        console.error('[AppWithPresence] Failed to reconnect after unlock:', error)
      }
    }

    // Clear the guard after 2 seconds (enough for activity events to propagate)
    setTimeout(() => {
      justUnlockedRef.current = false
    }, 2000)
  }

  return (
    <>
      <IdleWarningToast />
      <ScreenLockModal opened={isScreenLocked} onUnlock={handleUnlock} />
      {children}
    </>
  )
}

// Themed App Wrapper with Accessibility
function ThemedApp() {
  const colorSchemeKey = usePreferencesStore((state) => state.colorSchemeKey)
  const language = usePreferencesStore((state) => state.language)
  const reducedMotion = usePreferencesStore((state) => state.reducedMotion)
  const highContrast = usePreferencesStore((state) => state.highContrast)
  const fontSize = usePreferencesStore((state) => state.fontSize)
  const strongFocus = usePreferencesStore((state) => state.strongFocus)
  const grayscaleMode = usePreferencesStore((state) => state.grayscaleMode)
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing)
  const letterSpacing = usePreferencesStore((state) => state.letterSpacing)
  const uiSize = usePreferencesStore((state) => state.uiSize)

  const colorSchemeConfig = getColorScheme(colorSchemeKey)
  // Use the color scheme's primaryColor - this takes precedence over legacy accentColor
  const primaryColor = colorSchemeConfig?.primaryColor || 'indigo'

  const theme = useMemo(
    () => ({
      ...appTheme,
      primaryColor,
    }),
    [primaryColor],
  )

  useEffect(() => {
    document.documentElement.lang = language
    document.documentElement.classList.toggle('reduced-motion', Boolean(reducedMotion))
    document.documentElement.classList.toggle('high-contrast', Boolean(highContrast))
    document.documentElement.classList.toggle('strong-focus', Boolean(strongFocus))
    document.documentElement.classList.toggle('grayscale-mode', Boolean(grayscaleMode))
    document.documentElement.setAttribute('data-ui-size', uiSize || 'regular')
    document.documentElement.setAttribute('data-line-spacing', lineSpacing || 'regular')
    document.documentElement.setAttribute('data-letter-spacing', letterSpacing || 'normal')
    const scale =
      fontSize === 'xs'
        ? '0.9'
        : fontSize === 'sm'
          ? '0.95'
          : fontSize === 'lg'
            ? '1.1'
            : fontSize === 'xl'
              ? '1.2'
              : '1'
    document.documentElement.style.setProperty('--app-font-scale', scale)
  }, [language, reducedMotion, highContrast, fontSize, strongFocus, grayscaleMode, uiSize, lineSpacing, letterSpacing])

  return (
    <ThemeProvider theme={theme}>
      <CCMSProvider config={ccmsConfig}>
        <QueryClientProvider client={queryClient}>
          <AppWithAuth>
            <AppWithPreferenceLoader>
              <AppWithDeviceRegistration>
                <AppWithPresence>
                  <AlertProvider>
                    <ModalsProvider>
                      <BrowserRouter>
                        {/* In-app help: progress, tours, the help panel and Ask AI (needs the router, query client and auth). */}
                        <HelpProvider>
                          <App />
                        </HelpProvider>
                      </BrowserRouter>
                    </ModalsProvider>
                  </AlertProvider>
                </AppWithPresence>
              </AppWithDeviceRegistration>
            </AppWithPreferenceLoader>
          </AppWithAuth>
        </QueryClientProvider>
      </CCMSProvider>
    </ThemeProvider>
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ColorSchemeScript defaultColorScheme="auto" />
    <PreferencesProvider>
      <ThemedApp />
    </PreferencesProvider>
  </React.StrictMode>,
)
